import React, {useState} from 'react';
import moment from 'moment';
import isString from 'lodash/isString';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Stack from '@mui/material/Stack';

import { useUpdateVisitorMutation } from '../../redux/services/visitorApi';
import NewOrExistingPatient from './NewOrExistingPatient';
import PatientSchedulingLocationForm from './PatientSchedulingLocationForm';
import PatientSchedulingServiceForm from './PatientSchedulingServiceForm';
import PatientSchedulingSearchMethodForm from './PatientSchedulingSearchMethodForm';
import PatientSchedulingProviderForm from './PatientSchedulingProviderForm';
import PatientSchedulingLocationTypeForm from './PatientSchedulingLocationTypeForm';

const steps = ({
  providerId,
  next,
  setLocation,
  setService,
  setSearchMethod,
  setProviderId,
  setProviderAvailabilityId,
  saveDate,
  saveTime,
  saveSessionTypeId,
  saveProviderAvailabilityId,
  saveLocationType,
  saveLocationId,
  saveTimeCreated,
  saveScheduleManually,
  saveProviderId,
  saveServiceType,
  hideFirstStep = false,
  navigate,
  afterSubmit,
  updateVisitor,
}) => [
  {
    padding: '2em',
    Component: NewOrExistingPatient,
    onSubmit: (e, isNewPatient) => {
      if (isNewPatient) {
        next();
      } else {
        navigate('/login?redirectPath=/clients/booking');
      }
    },
  },
  {
    padding: '2em',
    Component: PatientSchedulingSearchMethodForm,
    onSubmit: async (e, searchMethod) => {
      setSearchMethod(searchMethod);
      next();
      await updateVisitor({
        scheduling_step: 'SELECTED_SEARCH_METHOD',
        search_method: searchMethod,
      });
    },
  },
  {
    padding: '2em',
    Component: PatientSchedulingLocationForm,
    onSubmit: async (e, address) => {
      if (address) {
        setLocation(address);
      } else {
        setLocation(e?.target?.value);
      }
      next();
      await updateVisitor({
        scheduling_step: 'SELECTED_LOCATION',
        lat: address?.lat || e?.target?.value?.lat,
        lon: address?.lon || e?.target?.value?.lon,
      });
    },
  },
  {
    padding: '2em',
    Component: PatientSchedulingServiceForm,
    onSubmit: async (e, serviceCat) => {
      setService(serviceCat);
      saveServiceType(serviceCat);
      next();
      await updateVisitor({
        scheduling_step: 'SELECTED_SERVICE_CATEGORY',
        service_category: serviceCat,
      });
    },
  },
  {
    padding: '0em',
    Component: PatientSchedulingProviderForm,
    onSubmit: async (e, {
      isoString, 
      providerId, 
      providerAvailabilityId, 
      sessionTypeId,
      scheduleManually,
    }) => {
      // The form stores its state in redux automatically
      setProviderId(providerId);
      if (scheduleManually) {
        saveScheduleManually(true);
        saveDate(null);
        saveTime(null);
        saveSessionTypeId(null);
        saveProviderAvailabilityId(null);
        setProviderAvailabilityId(null);
        saveProviderId(providerId);
        await updateVisitor({
          provider_id: providerId,
          did_find_providers: true,
          scheduling_step: 'COMPLETED_PRESIGNUP',
        });
        if (afterSubmit) afterSubmit({providerId});
      } else {
        const dateStr = moment(isoString).format('MM/DD/yyyy');
        const timeStr = moment(isoString).format('hh:mm a');
        saveDate(dateStr);
        saveTime(timeStr);
        saveProviderAvailabilityId(providerAvailabilityId);
        setProviderAvailabilityId(providerAvailabilityId);
        saveProviderId(providerId);
        saveSessionTypeId(sessionTypeId);
        saveScheduleManually(false);
        await updateVisitor({
          scheduling_step: 'SELECTED_TIME',
          session_time_requested: moment(isoString).toISOString(),
          provider_id: providerId,
          did_find_providers: true,
        });
        next();
      }
    },
  },
  {
    padding: '2em',
    Component: PatientSchedulingLocationTypeForm,
    onSubmit: async (e, formData) => {
      const {location_type, location_id} = formData;
      saveLocationType(location_type);
      saveLocationId(location_id);
      saveTimeCreated(moment().toISOString());
      let trackedUpdates = {
        scheduling_step: 'COMPLETED_PRESIGNUP',
      };
      if (isString(location_type)) {
        trackedUpdates.location_type = location_type;
      }
      if (location_id) {
        trackedUpdates.location_id = location_id;
      }
      await updateVisitor(trackedUpdates);
      if (afterSubmit) afterSubmit({providerId});
      // next();
    },
  },
];

const PublicPatientScheduling = props => {
  const {stepIndex, setStepIndex} = props;
  const [location, setLocation] = useState(null);
  const [serviceCategory, setService] = useState(null);
  const [searchMethod, setSearchMethod] = useState(null);
  const [providerId, setProviderId] = useState(null);
  const [providerAvailabilityId, setProviderAvailabilityId] = useState(null);
  const [updateVisitor] = useUpdateVisitorMutation();
  const navigate = useNavigate();
  const next = () => setStepIndex(stepIndex + 1);
  console.log('UUID', props.uuid);
  const stepConfig = steps({
    providerId,
    location,
    next,
    setLocation,
    setService,
    setSearchMethod,
    setProviderId,
    setProviderAvailabilityId,
    saveDate: props.saveDate,
    saveTime: props.saveTime,
    saveSessionTypeId: props.saveSessionTypeId,
    saveLocationId: props.saveLocationId,
    saveTimeCreated: props.saveTimeCreated,
    saveProviderId: props.saveProviderId,
    saveProviderAvailabilityId: props.saveProviderAvailabilityId,
    saveLocationType: props.saveLocationType,
    saveServiceType: props.saveServiceType,
    saveScheduleManually: props.saveScheduleManually,
    hideFirstStep: props.hideFirstStep,
    navigate,
    afterSubmit: props.afterSubmit,
    updateVisitor: async params => await updateVisitor({uuid: props.uuid, ...params}),
  })
  .filter((s, i) => i !== 0 || !props?.hideFirstStep)
  .map((s, i) => ({...s, stepIndex: i, stepNum: i - 1}))
  .find(step => step.stepIndex === stepIndex);
  const childProps = {
    stepIndex,
    location, 
    serviceCategory,
    searchMethod,
    providerAvailabilityId,
    onSubmit: stepConfig?.onSubmit,
    onChangeSearchMethod: (e, val) => setSearchMethod(val),
  };
  return (
    // <Stack alignItems='center' pr={0}>
      <Box 
        style={{
          marginTop: '0em',
          padding: props?.noPadding ? null : stepConfig.padding,
          // paddingTop: stepIndex === 0 ? '2em' : '0em',
          // boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
          // borderRadius: '16px',
          // backgroundColor: '#ffffff',
          // border: '1px solid rgb(240,240,240)',
          width: '100%',
        }}
      >

        {stepIndex > 0 &&
          <Button 
            onClick={() => setStepIndex(stepIndex - 1)} 
            size='small'
            startIcon={<ChevronLeftIcon/>}
            style={
              stepConfig?.padding === '0em'
              ? {marginLeft: '0em', marginTop: '0em'}
              : {} 
            }
          >
            Back
          </Button>
        }
        <stepConfig.Component {...childProps} />
      </Box>
    // </Stack>
  );
};

export default PublicPatientScheduling;